import React from 'react';
import { Helmet } from 'react-helmet';
import HomeCarousel from '../../components/HomeCarousel/HomeCarousel';
import styles from './Home.module.css';

// Hover images are used as CSS backgrounds on hover — fine as-is.
import visionHover from '../../assets/vision.jpg';
import missionHover from '../../assets/mission.jpeg';

export default function Home() {
  // Partners: render from /public/optimized to avoid importing large PNG/JPGs into the bundle.
  const partnerSlugs = [
    { slug: 'hyatt-logo', label: 'Hyatt' },
    { slug: 'radisson', label: 'Radisson' },
    { slug: 'wyndam-removebg-preview', label: 'Wyndham' },
    { slug: 'rmada', label: 'Ramada' },
    { slug: 'swiss', label: 'swiss' },
    { slug: 'output-onlinepngtools', label: 'Partner' },
  ];

  const infoCards = [
    {
      title: 'Our Vision',
      body:
        'To create a vibrant, inclusive community where international women in Uzbekistan feel connected, supported, and empowered.',
      hoverImg: visionHover,
    },
    {
      title: 'Our Mission',
      body:
        'To foster friendship, cultural exchange, and personal growth through social connection, support, and community engagement.',
      hoverImg: missionHover,
    },
  ];

  return (
    <>
      <Helmet>
        <title>TWIG • Home</title>
        <meta
          name="description"
          content="Welcome to TWIG – connect and support international women in Uzbekistan."
        />
        {/* Preload LCP hero with responsive AVIF set */}
        <link
          rel="preload"
          as="image"
          href="/optimized/carousel/carousel-800.jpg"
          imagesrcset="/optimized/carousel/carousel-800.avif 800w, /optimized/carousel/carousel-1200.avif 1200w, /optimized/carousel/carousel-1600.avif 1600w"
          imagesizes="100vw"
          fetchpriority="high"
        />
      </Helmet>

      {/* 1) Hero Carousel */}
      <section className={`${styles.hero} homeHero`}>
        <HomeCarousel />
      </section>

      {/* 2) Welcome Banner */}
      <section className={styles.welcomeBanner}>
        <div className="container">
          <h1>Welcome to TWIG</h1>
          <p>Your community for international women in Uzbekistan.</p>
          <a href="/membership" className={styles.cta}>
            Apply now
          </a>
        </div>
      </section>

      {/* 3) What is TWIG section */}
      <section className={styles.whatSection}>
        <div className="container">
          <h2>What is TWIG</h2>
          <div className={styles.whatText}>
            <p>
              The Tashkent Women's International Group (TWIG) has grown with time,
              but our foundation remains the same: a welcoming community of
              international women in Uzbekistan, dedicated to connection,
              mutual support, and cross-cultural friendship.
            </p>
            <p>
              We host monthly cultural meet-ups, professional workshops, and
              charitable initiatives, always in a spirit of inclusivity and
              compassion. Whether you’re new to Tashkent or a long-term resident,
              TWIG offers a place to feel at home.
            </p>
          </div>
        </div>
      </section>

      {/* 4) Vision & Mission Cards */}
      <section className={styles.infoSection}>
        <div className="container">
          <div className={styles.cards}>
            {infoCards.map(({ title, body, hoverImg }) => (
              <div
                key={title}
                className={styles.card}
                style={{ '--hover-img': `url(${hoverImg})` }}
              >
                <h2>{title}</h2>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5) Partners Banner */}
      <section className={styles.partnersBanner}>
        <div className="container">
          <h2>Our Partners</h2>
        </div>
      </section>

      {/* 6) Partners Grid (responsive, modern formats) */}
      <section className={styles.partnersSection}>
        <div className="container">
          <div className={styles.partnersGrid}>
            {partnerSlugs.map(({ slug, label }) => (
              <div key={slug} className={styles.partner}>
                <picture>
                  <source
                    type="image/avif"
                    srcSet={`/optimized/partners/${slug}/${slug}-80.avif 80w, /optimized/partners/${slug}/${slug}-160.avif 160w, /optimized/partners/${slug}/${slug}-320.avif 320w`}
                  />
                  <source
                    type="image/webp"
                    srcSet={`/optimized/partners/${slug}/${slug}-80.webp 80w, /optimized/partners/${slug}/${slug}-160.webp 160w, /optimized/partners/${slug}/${slug}-320.webp 320w`}
                  />
                  <img
                    src={`/optimized/partners/${slug}/${slug}-80.png`}
                    srcSet={`/optimized/partners/${slug}/${slug}-80.png 80w, /optimized/partners/${slug}/${slug}-160.png 160w, /optimized/partners/${slug}/${slug}-320.png 320w`}
                    sizes="(max-width: 480px) 120px, 160px"
                    alt={`${label} logo`}
                    width="160"
                    height="48"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
